import React, { useContext } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiHeart,
  FiMapPin,
  FiMessageCircle,
  FiRefreshCw,
  FiShield,
  FiTruck,
} from "react-icons/fi";
import "../css/main.scss";
import productData from "../Datas/productData";
import siteConfig from "../Datas/siteConfig";
import { CartContext } from "./CartContext";

const currencyFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
});

const wholeCurrencyFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
  maximumFractionDigits: 0,
});

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const hideBrokenImage = (event) => {
  const image = event.currentTarget;
  image.hidden = true;
  image.parentElement?.classList.add("is-image-missing");
};

const getProductImage = (index) =>
  productData[index]?.images?.[1] || productData[index]?.image || "";

const categoryBlueprints = [
  { title: "Waterproof jackets", index: 0, fallback: "Waterproof Jackets" },
  { title: "Fleece & midlayers", index: 6, fallback: "Fleece & Midlayers" },
  { title: "Base layers & tops", index: 10, fallback: "Tops" },
  { title: "Trousers & shorts", index: 14, fallback: "Trousers" },
  { title: "Walking footwear", index: 18, fallback: "Footwear" },
  { title: "Packs & equipment", index: 21, fallback: "Equipment" },
];

function EditorialProductCard({ product, isWishlisted, onToggleWishlist }) {
  if (!product) return null;

  const price = Number(product.price);
  const compareAtPrice = Number(product.compareAtPrice);

  return (
    <article className="jw-home-product-card">
      <div className="jw-home-product-card__media">
        {product.badge && (
          <span className="jw-home-product-card__badge">{product.badge}</span>
        )}
        <button
          className="jw-home-product-card__wish"
          type="button"
          aria-label={`${isWishlisted ? "Remove" : "Add"} ${product.name} ${
            isWishlisted ? "from" : "to"
          } wishlist`}
          aria-pressed={isWishlisted}
          onClick={() => onToggleWishlist(product.id)}
        >
          <FiHeart aria-hidden="true" />
        </button>
        <Link
          to={`/shopDetails/${product.id}`}
          onClick={scrollToTop}
          aria-label={`View ${product.name}`}
        >
          <img
            src={product.image || product.images?.[0]}
            alt={product.name}
            loading="lazy"
            onError={hideBrokenImage}
          />
        </Link>
      </div>
      <div className="jw-home-product-card__body">
        <p>{product.category}</p>
        <h3>
          <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
            {product.name}
          </Link>
        </h3>
        <div className="jw-home-product-card__price">
          <span>{currencyFormatter.format(price || 0)}</span>
          {compareAtPrice > price && (
            <del>{currencyFormatter.format(compareAtPrice)}</del>
          )}
        </div>
      </div>
    </article>
  );
}

function Main() {
  const cartContext = useContext(CartContext);
  const wishlistItems = cartContext?.wishlistItems || [];
  const toggleWishlist = cartContext?.toggleWishlist || (() => {});
  const featuredProducts = productData.slice(0, 4);
  const brandName = siteConfig.brandName || "Outdoor store";

  const categories = categoryBlueprints.map((category) => {
    const product = productData[category.index];
    return {
      ...category,
      image: getProductImage(category.index),
      query: product?.category || category.fallback,
    };
  });

  return (
    <main className="jw-home" aria-label={`${brandName} home`}>
      <section className="jw-home-hero" aria-labelledby="home-hero-title">
        <div className="jw-home-hero__media">
          <img
            src="/img/hero-trail.jpg"
            alt="Hikers crossing a rugged mountain trail"
            onError={hideBrokenImage}
          />
        </div>
        <div className="jw-home-hero__shade" />
        <div className="jw-home-hero__content">
          <p className="jw-home-eyebrow">Built for the way outside</p>
          <h1 id="home-hero-title">Weather changes. Keep moving.</h1>
          <p>
            Technical layers, dependable footwear and trail-ready equipment for
            every forecast.
          </p>
          <div className="jw-home-hero__actions" aria-label="Shop by audience">
            <Link to="/shop?audience=Men" onClick={scrollToTop}>
              Shop men
            </Link>
            <Link to="/shop?audience=Women" onClick={scrollToTop}>
              Shop women
            </Link>
            <Link to="/shop?audience=Kids" onClick={scrollToTop}>
              Shop kids
            </Link>
          </div>
        </div>
      </section>

      <section className="jw-home-intro jw-home-shell" aria-labelledby="intro-title">
        <p className="jw-home-eyebrow">Ready when the weather turns</p>
        <h2 id="intro-title">More outside. Whatever the forecast.</h2>
        <p>
          Discover protective outdoor clothing designed for long days, sudden
          showers and every adventure in between.
        </p>
      </section>

      <section className="jw-home-categories jw-home-shell" aria-labelledby="categories-title">
        <div className="jw-home-section-heading">
          <div>
            <p className="jw-home-eyebrow">Find your next layer</p>
            <h2 id="categories-title">Shop by category</h2>
          </div>
          <Link className="jw-home-text-link" to="/shop" onClick={scrollToTop}>
            Shop all <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="jw-home-categories__grid">
          {categories.map((category) => (
            <Link
              className="jw-home-category-card"
              key={category.title}
              to={`/shop?category=${encodeURIComponent(category.query)}`}
              onClick={scrollToTop}
            >
              <span className="jw-home-category-card__image">
                {category.image && (
                  <img
                    src={category.image}
                    alt=""
                    loading="lazy"
                    onError={hideBrokenImage}
                  />
                )}
              </span>
              <span className="jw-home-category-card__label">
                {category.title}
                <FiArrowRight aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="jw-home-rainwear" aria-labelledby="rainwear-title">
        <div className="jw-home-shell">
          <div className="jw-home-section-heading jw-home-section-heading--light">
            <div>
              <p className="jw-home-eyebrow">Dry by design</p>
              <h2 id="rainwear-title">Rainwear for every route</h2>
            </div>
            <p>
              Breathable protection, considered details and movement-first fits.
            </p>
          </div>
          <div className="jw-home-rainwear__grid">
            {[
              {
                audience: "Women",
                title: "Protection without the bulk",
                copy: "Light, packable shells made for fast-changing days.",
                image: "/img/story-women-rain.jpg",
              },
              {
                audience: "Men",
                title: "Built to outlast the downpour",
                copy: "Hard-wearing layers for exposed trails and everyday miles.",
                image: "/img/story-men-rain.jpg",
              },
            ].map((story) => (
              <article className="jw-home-story-card" key={story.audience}>
                <div className="jw-home-story-card__media">
                  {story.image && (
                    <img
                      src={story.image}
                      alt=""
                      loading="lazy"
                      onError={hideBrokenImage}
                    />
                  )}
                </div>
                <div className="jw-home-story-card__content">
                  <p>{story.audience}</p>
                  <h3>{story.title}</h3>
                  <span>{story.copy}</span>
                  <Link
                    to={`/shop?audience=${encodeURIComponent(
                      story.audience
                    )}&category=${encodeURIComponent(categories[0].query)}`}
                    onClick={scrollToTop}
                  >
                    Shop waterproofs <FiArrowRight aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="jw-home-featured jw-home-shell" aria-labelledby="featured-title">
        <div className="jw-home-section-heading">
          <div>
            <p className="jw-home-eyebrow">Trail favourites</p>
            <h2 id="featured-title">Made to go further</h2>
          </div>
          <Link className="jw-home-text-link" to="/shop" onClick={scrollToTop}>
            Explore all products <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="jw-home-featured__grid">
          {featuredProducts.map((product) => (
            <EditorialProductCard
              key={product.id}
              product={product}
              isWishlisted={wishlistItems.includes(product.id)}
              onToggleWishlist={toggleWishlist}
            />
          ))}
        </div>
      </section>

      <section className="jw-home-editorial jw-home-shell" aria-label="Outdoor advice and membership">
        <article className="jw-home-guide">
          <div className="jw-home-guide__media">
            <img
              src="/img/guide-layering.jpg"
              alt="Technical outdoor layers ready for a day on the trail"
              loading="lazy"
              onError={hideBrokenImage}
            />
          </div>
          <div className="jw-home-guide__content">
            <p className="jw-home-eyebrow">How to layer</p>
            <h2>One system. Any weather.</h2>
            <p>
              Learn how base, mid and outer layers work together to keep you
              comfortable from the first climb to the final mile.
            </p>
            <Link to="/shop?activity=Hiking" onClick={scrollToTop}>
              Build your layering system <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </article>

        <article className="jw-home-member">
          <div>
            <p className="jw-home-eyebrow">Join the pack</p>
            <h2>More adventure. More rewards.</h2>
            <p>
              Be first to hear about new gear, field stories and members-only
              offers, delivered with no clutter.
            </p>
          </div>
          <Link to="/contact" onClick={scrollToTop}>
            Become a member <FiArrowRight aria-hidden="true" />
          </Link>
          <FiMapPin className="jw-home-member__mark" aria-hidden="true" />
        </article>
      </section>

      <section className="jw-home-services" aria-label="Customer services">
        <div className="jw-home-shell jw-home-services__grid">
          {[
            {
              icon: FiTruck,
              title: "Free standard delivery",
              copy: `On orders over ${wholeCurrencyFormatter.format(
                siteConfig.freeShippingThreshold
              )}`,
            },
            {
              icon: FiRefreshCw,
              title: "Free returns",
              copy: `Within ${siteConfig.returnsWindow} days`,
            },
            {
              icon: FiShield,
              title: "Built to last",
              copy: "Quality you can rely on",
            },
            {
              icon: FiMessageCircle,
              title: "Expert support",
              copy: "Here when you need us",
            },
          ].map(({ icon: Icon, title, copy }) => (
            <div className="jw-home-service" key={title}>
              <Icon aria-hidden="true" />
              <div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Main;
